import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useOrder } from '../context/OrderContext';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';

const EMPTY_PROSPECT = { ragione_sociale: '', nome: '', cognome: '', email: '', telefono: '', piva: '' };

// Partita IVA senza spazi/simboli e senza prefisso IT
const normalizeVat = (value) => String(value || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase().replace(/^IT/, '');

const ClientSelectionModal = ({ isOpen, onClose, clients, onSelect, allowNewClient = false }) => {
    const { dispatch } = useOrder();
    const { user } = useAuth();
    const [view, setView] = useState('list'); // 'list' | 'new'
    const [prospect, setProspect] = useState(EMPTY_PROSPECT);
    const [prospectErrors, setProspectErrors] = useState({});
    const [existingClient, setExistingClient] = useState(null); // cliente gia' in anagrafica con la stessa P.IVA
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('CompanyName'); // CompanyName, CustSupp, Salesperson
    const [sortOrder, setSortOrder] = useState('asc'); // asc, desc
    const [filterSource, setFilterSource] = useState('all'); // all, mine, linked
    const [filterPriceList, setFilterPriceList] = useState('all');

    // Get the current agent's Salesperson code
    const currentAgentCode = user?.agent_data?.Salesperson || user?.id;

    // Get unique price lists for filter dropdown
    const priceLists = useMemo(() => {
        if (!clients) return [];
        const lists = [...new Set(clients.map(c => c.PriceList).filter(Boolean))];
        return lists.sort();
    }, [clients]);

    // Get unique agents for display
    const agentsList = useMemo(() => {
        if (!clients) return [];
        const agents = {};
        clients.forEach(c => {
            if (c.Salesperson && !agents[c.Salesperson]) {
                agents[c.Salesperson] = c.NameSalesPerson || c.Salesperson;
            }
        });
        return agents;
    }, [clients]);

    // Check if a client belongs to a linked agent (not the current agent)
    const isLinkedAgentClient = useCallback((client) => {
        return client.Salesperson && client.Salesperson !== currentAgentCode;
    }, [currentAgentCode]);

    // Filter and sort clients
    const filteredClients = useMemo(() => {
        if (!clients) return [];

        let result = [...clients];

        // Filter by search term
        if (searchTerm) {
            const lowerSearch = searchTerm.toLowerCase();
            result = result.filter(client =>
                (client.CustSupp && client.CustSupp.toLowerCase().includes(lowerSearch)) ||
                (client.CompanyName && client.CompanyName.toLowerCase().includes(lowerSearch)) ||
                (client.NameSalesPerson && client.NameSalesPerson.toLowerCase().includes(lowerSearch)) ||
                (client.Salesperson && client.Salesperson.toLowerCase().includes(lowerSearch)) ||
                (client.TaxIdNumber && client.TaxIdNumber.toLowerCase().includes(lowerSearch))
            );
        }

        // Filter by source (mine/linked)
        if (filterSource === 'mine') {
            result = result.filter(client => !isLinkedAgentClient(client));
        } else if (filterSource === 'linked') {
            result = result.filter(client => isLinkedAgentClient(client));
        }

        // Filter by price list
        if (filterPriceList !== 'all') {
            result = result.filter(client => client.PriceList === filterPriceList);
        }

        // Sort
        result.sort((a, b) => {
            let valA = a[sortBy] || '';
            let valB = b[sortBy] || '';

            if (typeof valA === 'string') valA = valA.toLowerCase();
            if (typeof valB === 'string') valB = valB.toLowerCase();

            if (sortOrder === 'asc') {
                return valA < valB ? -1 : valA > valB ? 1 : 0;
            } else {
                return valA > valB ? -1 : valA < valB ? 1 : 0;
            }
        });

        return result;
    }, [clients, searchTerm, filterSource, filterPriceList, sortBy, sortOrder, isLinkedAgentClient]);

    // Count by source
    const counts = useMemo(() => {
        if (!clients) return { total: 0, mine: 0, linked: 0 };
        const mine = clients.filter(c => !isLinkedAgentClient(c)).length;
        return {
            total: clients.length,
            mine,
            linked: clients.length - mine
        };
    }, [clients, isLinkedAgentClient]);

    // Reset all filters
    const resetFilters = () => {
        setSearchTerm('');
        setSortBy('CompanyName');
        setSortOrder('asc');
        setFilterSource('all');
        setFilterPriceList('all');
    };

    // Toggle sort order
    const toggleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortOrder('asc');
        }
    };

    // Chiudendo il modal si riparte sempre dall'elenco con il modulo vuoto
    useEffect(() => {
        if (!isOpen) {
            setView('list');
            setProspect(EMPTY_PROSPECT);
            setProspectErrors({});
            setExistingClient(null);
            setSaving(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleSelectClient = (client) => {
        dispatch({ type: 'SET_CUSTOMER', payload: client });
        if (onSelect) {
            onSelect(client);
        } else {
            onClose();
        }
    };

    const updateProspect = (field, value) => {
        setProspect((prev) => ({ ...prev, [field]: value }));
        if (prospectErrors[field]) setProspectErrors((prev) => ({ ...prev, [field]: null }));
        if (field === 'piva') setExistingClient(null);
    };

    const validateProspect = () => {
        const errors = {};
        if (!prospect.ragione_sociale.trim()) errors.ragione_sociale = 'Inserisci la ragione sociale';
        if (!prospect.nome.trim()) errors.nome = 'Inserisci il nome';
        if (!prospect.cognome.trim()) errors.cognome = 'Inserisci il cognome';
        if (!prospect.email.trim()) errors.email = 'Inserisci l\'email';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(prospect.email.trim())) errors.email = 'Email non valida';
        if (prospect.piva.trim() && !/^\d{11}$/.test(normalizeVat(prospect.piva))) errors.piva = 'La partita IVA deve avere 11 cifre';
        setProspectErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreateProspect = async (e) => {
        e.preventDefault();
        if (saving || !validateProspect()) return;

        // Controllo immediato sui clienti gia' visibili all'agente
        const vat = normalizeVat(prospect.piva);
        if (vat) {
            const found = (clients || []).find((c) => normalizeVat(c.TaxIdNumber) === vat);
            if (found) {
                setExistingClient(found);
                return;
            }
        }

        setSaving(true);
        try {
            const response = await api.createProspect({
                ragione_sociale: prospect.ragione_sociale.trim(),
                nome: prospect.nome.trim(),
                cognome: prospect.cognome.trim(),
                email: prospect.email.trim(),
                telefono: prospect.telefono.trim(),
                piva: vat,
                salesperson: currentAgentCode,
                created_by_name: user?.name || ''
            });
            if (response.status === 'success' && response.customer) {
                handleSelectClient(response.customer);
            } else {
                setProspectErrors({ form: response.message || 'Impossibile creare il cliente' });
            }
        } catch (err) {
            if (err && err.code === 'client_exists' && err.client) {
                const visible = (clients || []).find((c) => c.CustSupp === err.client.CustSupp);
                setExistingClient(visible || err.client);
            } else if (err && err.field) {
                setProspectErrors({ [err.field]: err.message });
            } else {
                setProspectErrors({ form: (err && err.message) || 'Impossibile creare il cliente' });
            }
        } finally {
            setSaving(false);
        }
    };

    const inputClass = (field) => `w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${prospectErrors[field] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`;

    if (view === 'new') {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                <form onSubmit={handleCreateProspect} noValidate className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[92vh] flex flex-col">
                    <div className="p-4 border-b flex justify-between items-center">
                        <div>
                            <h2 className="text-xl font-semibold">Nuovo cliente</h2>
                            <p className="text-sm text-gray-500 mt-1">Cliente non ancora in anagrafica: i dati saranno inviati nella mail dell'offerta o dell'ordine.</p>
                        </div>
                        <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 p-2" aria-label="Chiudi">
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    <div className="p-4 overflow-y-auto flex-grow space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Ragione sociale *</label>
                            <input type="text" autoFocus value={prospect.ragione_sociale} onChange={(e) => updateProspect('ragione_sociale', e.target.value)} className={inputClass('ragione_sociale')} maxLength={255} />
                            {prospectErrors.ragione_sociale && <p className="text-xs text-red-600 mt-1">{prospectErrors.ragione_sociale}</p>}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nome referente *</label>
                                <input type="text" value={prospect.nome} onChange={(e) => updateProspect('nome', e.target.value)} className={inputClass('nome')} maxLength={100} />
                                {prospectErrors.nome && <p className="text-xs text-red-600 mt-1">{prospectErrors.nome}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Cognome referente *</label>
                                <input type="text" value={prospect.cognome} onChange={(e) => updateProspect('cognome', e.target.value)} className={inputClass('cognome')} maxLength={100} />
                                {prospectErrors.cognome && <p className="text-xs text-red-600 mt-1">{prospectErrors.cognome}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                                <input type="email" inputMode="email" value={prospect.email} onChange={(e) => updateProspect('email', e.target.value)} className={inputClass('email')} maxLength={255} />
                                {prospectErrors.email && <p className="text-xs text-red-600 mt-1">{prospectErrors.email}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                                <input type="tel" inputMode="tel" value={prospect.telefono} onChange={(e) => updateProspect('telefono', e.target.value)} className={inputClass('telefono')} maxLength={50} />
                            </div>
                            <div className="sm:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Partita IVA <span className="text-gray-400 font-normal">(facoltativa)</span></label>
                                <input type="text" inputMode="numeric" value={prospect.piva} onChange={(e) => updateProspect('piva', e.target.value)} className={inputClass('piva')} maxLength={16} placeholder="11 cifre" />
                                {prospectErrors.piva && <p className="text-xs text-red-600 mt-1">{prospectErrors.piva}</p>}
                            </div>
                        </div>

                        {existingClient && (
                            <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg text-sm text-amber-900">
                                <p className="font-semibold">Questo cliente è già in anagrafica</p>
                                <p className="mt-1">
                                    <span className="font-mono">{existingClient.CustSupp}</span> · {existingClient.CompanyName}
                                </p>
                                {clients && clients.some((c) => c.CustSupp === existingClient.CustSupp) ? (
                                    <button
                                        type="button"
                                        onClick={() => handleSelectClient(clients.find((c) => c.CustSupp === existingClient.CustSupp))}
                                        className="mt-2 px-3 py-1.5 bg-amber-500 text-white rounded-lg font-medium hover:bg-amber-600"
                                    >
                                        Usa questo cliente
                                    </button>
                                ) : (
                                    <p className="mt-1 text-xs">Non è assegnato a te: chiedi all'amministrazione di assegnartelo.</p>
                                )}
                            </div>
                        )}

                        {prospectErrors.form && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{prospectErrors.form}</div>
                        )}
                    </div>

                    <div className="p-4 border-t flex justify-between items-center">
                        <button
                            type="button"
                            onClick={() => { setView('list'); setProspectErrors({}); setExistingClient(null); }}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            ← Torna all'elenco
                        </button>
                        <button
                            type="submit"
                            disabled={saving}
                            className="px-5 py-2 text-sm font-semibold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors disabled:opacity-50"
                        >
                            {saving ? 'Creazione...' : 'Crea cliente e continua'}
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    const hasActiveFilters = searchTerm || filterSource !== 'all' || filterPriceList !== 'all' || sortBy !== 'CompanyName';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
                    <div>
                        <h2 className="text-xl font-semibold">Seleziona Cliente</h2>
                        <p className="text-sm text-gray-500 mt-1">
                            {counts.total} clienti totali ({counts.mine} tuoi, {counts.linked} da agenti collegati)
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-gray-700 p-2"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Search and Filters */}
                <div className="p-4 border-b sticky top-[73px] bg-gray-50 z-10 space-y-3">
                    {allowNewClient && (
                        <button
                            type="button"
                            onClick={() => setView('new')}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-red-300 text-red-600 rounded-lg hover:bg-red-50 text-sm font-semibold transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Il cliente non è in elenco? Crea un nuovo cliente
                        </button>
                    )}

                    {/* Search bar */}
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Cerca per codice, ragione sociale, P.IVA o agente..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-2.5 pl-10 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                        <svg
                            className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>

                    {/* Filter buttons row */}
                    <div className="flex flex-wrap gap-2 items-center">
                        {/* Source filter tabs */}
                        <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
                            <button
                                onClick={() => setFilterSource('all')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filterSource === 'all'
                                    ? 'bg-red-500 text-white'
                                    : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                            >
                                Tutti ({counts.total})
                            </button>
                            <button
                                onClick={() => setFilterSource('mine')}
                                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filterSource === 'mine'
                                    ? 'bg-red-500 text-white'
                                    : 'text-gray-600 hover:bg-gray-100'
                                    }`}
                            >
                                Miei ({counts.mine})
                            </button>
                            {counts.linked > 0 && (
                                <button
                                    onClick={() => setFilterSource('linked')}
                                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${filterSource === 'linked'
                                        ? 'bg-blue-500 text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                        }`}
                                >
                                    <span className="flex items-center gap-1">
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                        </svg>
                                        Collegati ({counts.linked})
                                    </span>
                                </button>
                            )}
                        </div>

                        {/* Price list filter */}
                        {priceLists.length > 1 && (
                            <select
                                value={filterPriceList}
                                onChange={(e) => setFilterPriceList(e.target.value)}
                                className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-red-500 focus:border-red-500"
                            >
                                <option value="all">Tutti i listini</option>
                                {priceLists.map(list => (
                                    <option key={list} value={list}>Listino: {list}</option>
                                ))}
                            </select>
                        )}

                        {/* Sort buttons */}
                        <div className="flex items-center gap-1 ml-auto">
                            <span className="text-sm text-gray-500 mr-1">Ordina:</span>
                            <button
                                onClick={() => toggleSort('CompanyName')}
                                className={`px-2 py-1.5 text-sm rounded transition-colors flex items-center gap-1 ${sortBy === 'CompanyName' ? 'bg-gray-200 font-medium' : 'hover:bg-gray-100'
                                    }`}
                            >
                                Nome
                                {sortBy === 'CompanyName' && (
                                    <svg className={`w-4 h-4 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                )}
                            </button>
                            <button
                                onClick={() => toggleSort('CustSupp')}
                                className={`px-2 py-1.5 text-sm rounded transition-colors flex items-center gap-1 ${sortBy === 'CustSupp' ? 'bg-gray-200 font-medium' : 'hover:bg-gray-100'
                                    }`}
                            >
                                Codice
                                {sortBy === 'CustSupp' && (
                                    <svg className={`w-4 h-4 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                )}
                            </button>
                            <button
                                onClick={() => toggleSort('Salesperson')}
                                className={`px-2 py-1.5 text-sm rounded transition-colors flex items-center gap-1 ${sortBy === 'Salesperson' ? 'bg-gray-200 font-medium' : 'hover:bg-gray-100'
                                    }`}
                            >
                                Agente
                                {sortBy === 'Salesperson' && (
                                    <svg className={`w-4 h-4 ${sortOrder === 'desc' ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                                    </svg>
                                )}
                            </button>
                        </div>

                        {/* Reset filters */}
                        {hasActiveFilters && (
                            <button
                                onClick={resetFilters}
                                className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-1"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                Reset
                            </button>
                        )}
                    </div>
                </div>

                {/* Client list */}
                <div className="overflow-y-auto flex-grow p-4">
                    {filteredClients && filteredClients.length > 0 ? (
                        <div className="space-y-2">
                            {filteredClients.map((client) => {
                                const isFromLinkedAgent = isLinkedAgentClient(client);
                                return (
                                    <div
                                        key={client.CustSupp}
                                        onClick={() => handleSelectClient(client)}
                                        className={`p-4 rounded-lg cursor-pointer transition-all duration-200 border ${isFromLinkedAgent
                                            ? 'bg-blue-50 hover:bg-blue-100 border-blue-200 hover:border-blue-400 hover:shadow-md'
                                            : 'bg-gray-50 hover:bg-red-50 border-gray-200 hover:border-red-300 hover:shadow-md'
                                            }`}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex-grow min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className="font-mono font-semibold text-lg">{client.CustSupp}</p>
                                                    {isFromLinkedAgent && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                                                            </svg>
                                                            Collegato
                                                        </span>
                                                    )}
                                                    {client.PriceList && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                                            {client.PriceList}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-gray-700 mt-1 font-medium truncate">{client.CompanyName}</p>
                                                {client.TaxIdNumber && (
                                                    <p className="text-sm text-gray-500 mt-1">P.IVA: {client.TaxIdNumber}</p>
                                                )}
                                                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-gray-600">
                                                    {client.NameSalesPerson && (
                                                        <span className={`flex items-center gap-1 ${isFromLinkedAgent ? 'font-medium text-blue-700' : ''}`}>
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                            </svg>
                                                            {client.NameSalesPerson}
                                                        </span>
                                                    )}
                                                    {client.Payment && (
                                                        <span className="flex items-center gap-1">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                            </svg>
                                                            {client.Payment}
                                                        </span>
                                                    )}
                                                    {client.MAIL && (
                                                        <span className="flex items-center gap-1">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                            </svg>
                                                            {client.MAIL}
                                                        </span>
                                                    )}
                                                    {client.TELEFONO && (
                                                        <span className="flex items-center gap-1">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                            </svg>
                                                            {client.TELEFONO}
                                                        </span>
                                                    )}
                                                    {client.DiscountFormula && (
                                                        <span className="flex items-center gap-1">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                                                            </svg>
                                                            Sc. {client.DiscountFormula}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <svg
                                                className={`w-6 h-6 flex-shrink-0 ml-4 ${isFromLinkedAgent ? 'text-blue-500' : 'text-red-500'}`}
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <p className="text-gray-500 text-lg">
                                {searchTerm || filterSource !== 'all' || filterPriceList !== 'all'
                                    ? 'Nessun cliente trovato con i filtri selezionati'
                                    : 'Nessun cliente assegnato'}
                            </p>
                            {hasActiveFilters && (
                                <button
                                    onClick={resetFilters}
                                    className="mt-4 px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors inline-flex items-center gap-2"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Rimuovi filtri
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t sticky bottom-0 bg-white flex justify-between items-center">
                    <p className="text-sm text-gray-600">
                        <span className="font-semibold">{filteredClients.length}</span> {filteredClients.length === 1 ? 'cliente' : 'clienti'}
                        {filteredClients.length !== counts.total && ` su ${counts.total}`}
                    </p>
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    >
                        Chiudi
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ClientSelectionModal;
